import React from 'react'

import { Card } from 'primereact/card'
import { Skeleton } from 'primereact/skeleton'

import { OrderResponseBuyerInformation } from '../../data/models'
import { ConfigurableComponent } from '../../utils/wrapper'

interface DataInformation
  extends Omit<OrderResponseBuyerInformation, 'Qualifier'> {
  Plant?: string // Added for deliveryForecastDespatchAdvice
  UnloadingPoint?: string // Added for deliveryForecastDespatchAdvice
  VATNumber?: string // Added because this field is missing in invoiceInformation
  Qualifier?: string // Added because while loading this is undefined
}

interface Props {
  title?: string
  data?: DataInformation
  config: any
  isLoading?: boolean
}

export const CardTemplate: React.FC<Props> = ({
  title,
  data,
  config,
  isLoading,
}) => {
  const createLabel = (id: string) => (
    <label className="text-sm mb-0 w-full break-words" htmlFor={id}></label>
  )

  const createComponent = (id: string, data: any) => (
    <span id={id} className="break-words">
      {data || '-'}
    </span>
  )

  const createLoadingComponent = () => <Skeleton></Skeleton>

  // header
  const header = <h4 className="m-0 ml-3 pt-3">{title}</h4>

  // externalId
  const externalIdLabel = createLabel('externalId')
  const externalIdComponent = createComponent('externalId', data?.ExternalId)

  const externalIdWrapper = (
    <ConfigurableComponent
      config={config}
      component={externalIdComponent}
      label={externalIdLabel}
      loadingComponent={createLoadingComponent()}
      isLoading={isLoading}
      className="break-words overflow-wrap-anywhere"
    />
  )

  // company
  const companyLabel = createLabel('company')
  const companyComponent = createComponent('company', data?.Company)

  const companyWrapper = (
    <ConfigurableComponent
      config={config}
      component={companyComponent}
      label={companyLabel}
      loadingComponent={createLoadingComponent()}
      isLoading={isLoading}
      className="break-words overflow-wrap-anywhere"
    />
  )

  // department
  const departmentLabel = createLabel('department')
  const departmentComponent = createComponent('department', data?.Department)

  const departmentWrapper = (
    <ConfigurableComponent
      config={config}
      component={departmentComponent}
      label={departmentLabel}
      loadingComponent={createLoadingComponent()}
      isLoading={isLoading}
      className="break-words overflow-wrap-anywhere"
    />
  )

  // name
  const nameLabel = createLabel('name')
  const nameComponent = createComponent('name', data?.Contact?.Name)

  const nameWrapper = (
    <ConfigurableComponent
      config={config}
      component={nameComponent}
      label={nameLabel}
      loadingComponent={createLoadingComponent()}
      isLoading={isLoading}
      className="break-words overflow-wrap-anywhere"
    />
  )

  // phone
  const phoneLabel = createLabel('phone')
  const phoneComponent = createComponent('phone', data?.Contact?.Phone)

  const phoneWrapper = (
    <ConfigurableComponent
      config={config}
      component={phoneComponent}
      label={phoneLabel}
      loadingComponent={createLoadingComponent()}
      isLoading={isLoading}
      className="break-words overflow-wrap-anywhere"
    />
  )

  // email
  const emailLabel = createLabel('email')
  const emailComponent = createComponent('email', data?.Contact?.Email)

  const emailWrapper = (
    <ConfigurableComponent
      config={config}
      component={emailComponent}
      label={emailLabel}
      loadingComponent={createLoadingComponent()}
      isLoading={isLoading}
      className="break-words overflow-wrap-anywhere"
    />
  )

  // address
  const addressLabel = createLabel('address')
  const adress = (
    <>
      {data?.Address?.Street} {data?.Address?.Number} <br />
      {data?.Address?.PostalCode} {data?.Address?.City}
    </>
  )
  const addressComponent = createComponent('address', adress)

  const addressWrapper = (
    <ConfigurableComponent
      config={config}
      component={addressComponent}
      label={addressLabel}
      loadingComponent={createLoadingComponent()}
      isLoading={isLoading}
      className="break-words overflow-wrap-anywhere"
    />
  )

  // countryCode
  const countryCodeLabel = createLabel('countryCode')
  const countryCodeComponent = createComponent(
    'countryCode',
    data?.Address?.CountryCode,
  )

  const countryCodeWrapper = (
    <ConfigurableComponent
      config={config}
      component={countryCodeComponent}
      label={countryCodeLabel}
      loadingComponent={createLoadingComponent()}
      isLoading={isLoading}
      className="break-words overflow-wrap-anywhere"
    />
  )

  // vatNumber
  const vatNumberLabel = createLabel('vatNumber')
  const vatNumberComponent = createComponent('vatNumber', data?.VATNumber)

  const vatNumberWrapper = (
    <ConfigurableComponent
      config={config}
      component={vatNumberComponent}
      label={vatNumberLabel}
      loadingComponent={createLoadingComponent()}
      isLoading={isLoading}
      className="break-words overflow-wrap-anywhere"
    />
  )

  // plant
  const plantLabel = createLabel('plant')
  const plantComponent = createComponent('plant', data?.Plant)

  const plantWrapper = (
    <ConfigurableComponent
      config={config}
      component={plantComponent}
      label={plantLabel}
      loadingComponent={createLoadingComponent()}
      isLoading={isLoading}
      className="break-words overflow-wrap-anywhere"
    />
  )

  // unloadingPoint
  const unloadingPointLabel = createLabel('unloadingPoint')
  const unloadingPointComponent = createComponent(
    'unloadingPoint',
    data?.UnloadingPoint,
  )

  const unloadingPointWrapper = (
    <ConfigurableComponent
      config={config}
      component={unloadingPointComponent}
      label={unloadingPointLabel}
      loadingComponent={createLoadingComponent()}
      isLoading={isLoading}
      className="break-words overflow-wrap-anywhere"
    />
  )

  return (
    <>
      {config.enabled !== false && (
        <div className="col-12 sm:col-6 lg:col-3">
          <Card header={header} className="h-full">
            {/* externalId */}
            {externalIdWrapper}

            {/* company */}
            {companyWrapper}

            {/* department */}
            {departmentWrapper}

            {/* name */}
            {nameWrapper}

            {/* phone */}
            {phoneWrapper}

            {/* email */}
            {emailWrapper}

            {/* address */}
            {addressWrapper}

            {/* countryCode */}
            {countryCodeWrapper}

            {/* vatNumber */}
            {vatNumberWrapper}

            {/* plantWrapper */}
            {data?.Plant && plantWrapper}

            {/* unloadingPointWrapper */}
            {data?.UnloadingPoint && unloadingPointWrapper}
          </Card>
        </div>
      )}
    </>
  )
}
